import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';

import { FHAntdDateTimePicker, FAntdTimePicker, FHButton, FBoxContainer } from '../../../../base';
import { applyTimeToDate, disabledFutureDate } from '../../../../../helpers';
import { useProcedureTimeRestrictions } from '../../../../../hooks';
import { getUtcOffset } from '../../../../../utils/common';
import { FORTA_ERROR, FORTA_WHITE } from '../../../../../utils/colors';
import { BackgroundTypes } from '../../../../../constants';
import {
  ASSESSMENT_BACKDATE_CONTAINER_SX,
  ASSESSMENT_BACKDATE_HEADER_SX,
  ASSESSMENT_BACKDATE_ROW_SX,
} from '../styles';

import type { Moment } from 'moment';
import type { AssessmentBackdateFormProps } from '../types';

function AssessmentBackdateForm({ assessment, onBackdate }: AssessmentBackdateFormProps) {
  const [backdatedDate, setBackdatedDate] = useState<Moment | null>(null);

  // Start time between 12:00 AM and 3:59 AM in the client's local time zone is rejected by the
  // backend, so block it on the frontend before the round-trip.
  const { timeToolTipOpen: isStartBetweenMidnightAnd4Am } = useProcedureTimeRestrictions(
    backdatedDate,
    assessment.duration,
    false,
    assessment.client?.street_address_zip ?? null,
  );

  useEffect(() => {
    setBackdatedDate(moment(assessment.date_start));
  }, [assessment]);

  const handleDateChange = (value: Moment | null) => {
    if (value) setBackdatedDate(value);
  };

  const handleTimeChange = (value: Moment | null) => {
    if (value && backdatedDate) {
      setBackdatedDate(applyTimeToDate(backdatedDate, value));
    }
  };

  const handleBackdateClick = () => {
    if (backdatedDate) {
      onBackdate(backdatedDate, getUtcOffset(backdatedDate));
    }
  };

  const isFutureStart = Boolean(backdatedDate && backdatedDate.isAfter(moment()));
  const isBackdateDisabled = isStartBetweenMidnightAnd4Am || isFutureStart;

  return (
    <FBoxContainer sx={ASSESSMENT_BACKDATE_CONTAINER_SX} headerStyle={ASSESSMENT_BACKDATE_HEADER_SX}>
      <Box sx={ASSESSMENT_BACKDATE_ROW_SX}>
        <Box mr={3}>
          <FHAntdDateTimePicker
            format="MM/DD/YY"
            inputHeight={40}
            sx={{ boxShadow: 'none' }}
            BackgroundTypes={BackgroundTypes.Colored}
            inputReadOnly
            allowClear={false}
            value={backdatedDate}
            disabledDate={disabledFutureDate}
            onSelect={handleDateChange}
            onChange={handleDateChange}
          />
        </Box>
        <FAntdTimePicker
          inputHeight={40}
          style={{ boxShadow: 'none', backgroundColor: FORTA_WHITE }}
          format="h:mm A"
          allowClear={false}
          showNow
          use12Hours
          coloredBackground
          value={backdatedDate}
          onChange={handleTimeChange}
          onSelect={handleTimeChange}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', ml: 3 }}>
          <FHButton onClick={handleBackdateClick} disabled={isBackdateDisabled}>
            Backdate Note
          </FHButton>
        </Box>
      </Box>
      {isFutureStart && (
        <Typography sx={{ color: FORTA_ERROR, mt: 1 }} textAlign="left">
          Date and time cannot be in the future.
        </Typography>
      )}
      {!isFutureStart && isStartBetweenMidnightAnd4Am && (
        <Typography sx={{ color: FORTA_ERROR, mt: 1 }} textAlign="left">
          Assessment start time cannot fall between 12:00 AM and 4:00 AM.
        </Typography>
      )}
    </FBoxContainer>
  );
}

export default AssessmentBackdateForm;
